
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { Emit } from "@/api/apiHelper";
import SubscriptionEntry from "@/models/subscription/SubscriptionEntry";
import SubscriptionPlan, {
  SubscriptionBillingType,
} from "@/models/subscription/SubscriptionPlan";
import AppBar from "./AppBar.vue";
import BaseView from "@/views/BaseView.vue";

@Component({
  components: { AppBar },
})
export default class NavigationDrawer extends BaseView {
  // 🚩 Flag for whether the navigation drawer is expanded or not
  private isOpen = false;

  // 🚩 Flag for whether the navigation drawer should be visible or not
  private drawer = false;

  // 🚩 Flag for whether the current user us authorized
  private isUserAuthorized: boolean = false;

  // 📦 The version of the current build
  private version: string | null = null;

  // 📦 The options that would be displayed in the navigation drawer middle section
  private options = [
    {
      icon: this.$store.state.userPreferences.liteMode
        ? "mdi-plus"
        : "mdi-robot",
      title: this.$store.state.userPreferences.liteMode ? "Invest" : "New Bot",
      desiredRoute: "/create",
    },
    {
      icon: "mdi-view-dashboard",
      title: "My Portfolio",
      desiredRoute: "/my-portfolio",
    },
    // TODO: 07/12/2020 Unhide it when backtesting can be usable by the users
    // { icon: "mdi-tune", title: "Backtesting", desiredRoute: "/backtest" },
    {
      icon: "mdi-wallet",
      title: "Wallet",
      desiredRoute: "/wallet",
    },
    {
      icon: "mdi-cogs",
      title: "Settings",
      desiredRoute: "/settings",
    },
    {
      icon: "mdi-comment-question",
      title: "Help Center",
      href: "https://oneclickcrypto.tawk.help",
    },
  ];

  // 📦 The user name of the current user
  private get username(): string {
    return this.$store.state.userData.username;
  }

  // 📦 The current subscription limitations
  private get currentSubscriptionPlan(): SubscriptionPlan {
    return this.$store.state.subscription.currentSubscriptionPlan;
  }

  // 📦 The current Stripe subscription
  private get currentStripeSubscription(): SubscriptionEntry {
    return this.$store.state.payments.currentSubscription;
  }

  // 📦 The name of the subscription the current user is on (if Stripe is available - it has priority)
  private get currentSubscriptionPlanName(): string | null {
    if (
      this.currentStripeSubscription !== undefined &&
      this.currentStripeSubscription != null
    ) {
      return this.currentStripeSubscription.productName;
    }
    if (
      this.currentSubscriptionPlan !== undefined &&
      this.currentSubscriptionPlan != null &&
      this.currentSubscriptionPlan.billingType === SubscriptionBillingType.Free
    ) {
      return this.currentSubscriptionPlan.type + " Plan";
    }
    return null;
  }

  protected async mounted() {
    super.mounted();

    if (!this.$store.state.userPreferences.liteMode) {
      this.options.splice(3, 0, {
        icon: "mdi-cards",
        title: "NFTs",
        desiredRoute: "/nft-collection",
      });

      this.options.splice(4, 0, {
        icon: "mdi-chart-bar",
        title: "Stats",
        desiredRoute: "/stats",
      });
    }

    this.$store.subscribe((mutation, state) => {
      if (mutation.type === "SET_IS_AUTHORIZED") {
        this.drawer =
          mutation.payload !== false &&
          mutation.payload !== null &&
          !this.$vuetify.breakpoint.mobile;
        this.isUserAuthorized =
          mutation.payload !== false && mutation.payload !== null;

        if (this.isUserAuthorized && this.version == null) {
          this.$store.dispatch("fetchCurrentOBTHoldingTier");
          this.fetchCurrentBuildVersion();
        }
      }
    });
  }

  /**
   * 🚀 Fetches the current build's version from the API
   */
  private fetchCurrentBuildVersion() {
    Vue.prototype.$api
      .get("/api/v1/version", null, Emit.None)
      .then((response: any) => {
        this.version = response.data;
      });
  }

  /**
   * 🚪 Functionality that cleans the current Vuex state and redirects the user to login page
   */
  private logout() {
    this.$store.dispatch("prepareUserForLogout");
    this.$router.push("/login");
  }

  /**
   * 🔃 Reloads the current page if the user is on the create bot page
   */
  private reloadPage() {
    window.location.reload();
  }
}
